import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    ActivityIndicator,
    Animated,
    Image,
    Keyboard,
    Platform,
    Pressable,
    ScrollView,
    StyleSheet,
    Text,
    TextInput as RNTextInput,
    View
} from "react-native";
import { Snackbar } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { SignatureModal } from "@/components/questions/SignatureModal";
import { formatAidePhone, showScheduledAidePhone } from "@/lib/aide-contact";
import {
    ApiError,
    fetchPerformanceEvaluation,
    fetchPerformanceEvaluationItems,
    savePerformanceEvaluation,
    type PerformanceEvaluationItem,
    type PerformanceEvaluationRatingInput
} from "@/lib/api";
import { logRocketRedactProps } from "@/lib/logrocket";
import { colors, gradients, radius, shadows, spacing } from "@/lib/theme";

interface Props {
    visitId: string;
    scheduledAideName?: string | null;
    /** The scheduled aide's phone (visit.caregiverPhoneNumber), shown under
     *  the name so the nurse can confirm she is evaluating the right aide. */
    scheduledAidePhone?: string | null;
    onComplete: () => void;
    onBack: () => void;
    readOnly: boolean;
}

type Method = "verbal" | "observation";
type RatingValue = "competent" | "needs_improvement";
type StepIndex = 0 | 1 | 2 | 3 | 4;

const ABSENCE_REASONS = [
    "Called out sick",
    "Running late",
    "No-show",
    "Already left",
    "Patient cancelled"
] as const;

const SUB_STEPS = [
    "Aide Presence",
    "General Duties",
    "Administrative Performance",
    "Comments",
    "Signatures"
] as const;

const INITIAL_STICKY_BAR_HEIGHT = 81;
const AUTOSAVE_DEBOUNCE_MS = 500;

function normalizeStr(s: string | null | undefined): string {
    return (s ?? "").trim();
}

function keyFor(item: Pick<PerformanceEvaluationItem, "sourceType" | "sourceId">) {
    return `${item.sourceType}:${item.sourceId}`;
}

function keyForRating(item: Pick<PerformanceEvaluationRatingInput, "sourceType" | "sourceId">) {
    return `${item.sourceType}:${item.sourceId}`;
}

function arraysEqual<T>(a: readonly T[], b: readonly T[]): boolean {
    if (a.length !== b.length) {
        return false;
    }
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) {
            return false;
        }
    }
    return true;
}

function normalizeRatingsForCompare(ratings: PerformanceEvaluationRatingInput[]) {
    return ratings
        .map((rating) => ({
            sourceType: rating.sourceType,
            sourceId: rating.sourceId,
            itemKey: rating.itemKey ?? null,
            category: rating.category,
            label: rating.label,
            order: rating.order,
            rating: rating.rating,
            methods: normalizeMethods(rating.methods).sort(),
            note: normalizeStr(rating.note)
        }))
        .sort((a, b) => keyForRating(a).localeCompare(keyForRating(b)));
}

type NormalizedRating = ReturnType<typeof normalizeRatingsForCompare>[number];

interface PerformanceServerSnapshot {
    isAidePresent: boolean | null;
    actualAideName: string;
    absenceReasonTags: string[];
    absenceReasonNote: string;
    generalComments: string;
    administrativeComments: string;
    supervisorComments: string;
    ratings: NormalizedRating[];
    rnSignatureData: string | null;
    caregiverSignatureData: string | null;
}

function groupItems(items: PerformanceEvaluationItem[]) {
    return {
        general: items.filter((item) => item.category === "general"),
        administrative: items.filter((item) => item.category === "administrative")
    };
}

function normalizeMethods(methods: unknown): Method[] {
    if (!Array.isArray(methods)) {
        return [];
    }
    return Array.from(
        new Set(
            methods.filter(
                (method): method is Method => method === "verbal" || method === "observation"
            )
        )
    );
}

function sanitizeRating(
    rating: PerformanceEvaluationRatingInput,
    visibleItemsByKey: Map<string, PerformanceEvaluationItem>
): PerformanceEvaluationRatingInput | null {
    const item = visibleItemsByKey.get(keyForRating(rating));
    if (!item) {
        return null;
    }
    return {
        sourceType: item.sourceType,
        sourceId: item.sourceId,
        itemKey: item.itemKey,
        category: item.category,
        label: item.label,
        order: item.sortOrder,
        rating: rating.rating === "needs_improvement" ? "needs_improvement" : "competent",
        methods: normalizeMethods(rating.methods),
        note: normalizeStr(rating.note)
    };
}

function computeResumeSubStep(
    existing: PerformanceServerSnapshot | null,
    grouped: ReturnType<typeof groupItems>,
    scheduledAideName: string | null | undefined
): StepIndex {
    if (!existing) {
        return 0;
    }
    const aideName = existing.actualAideName || normalizeStr(scheduledAideName);
    const absentWithReason =
        existing.isAidePresent === false &&
        (existing.absenceReasonTags.length > 0 || existing.absenceReasonNote !== "");
    const step0Done =
        existing.isAidePresent !== null &&
        (existing.isAidePresent === true ? aideName !== "" : absentWithReason);
    if (!step0Done || existing.isAidePresent === false) {
        return 0;
    }
    if (!areRowsComplete(grouped.general, existing.ratings)) {
        return 1;
    }
    if (!areRowsComplete(grouped.administrative, existing.ratings)) {
        return 2;
    }
    return 4;
}

function areRowsComplete(rows: PerformanceEvaluationItem[], ratings: NormalizedRating[]) {
    const ratingByKey = new Map(ratings.map((rating) => [keyForRating(rating), rating]));
    return rows.every((item) => {
        const rating = ratingByKey.get(keyFor(item));
        if (!rating) {
            return false;
        }
        if (rating.methods.length === 0) {
            return false;
        }
        if (rating.rating === "needs_improvement" && !rating.note) {
            return false;
        }
        return true;
    });
}

function initialsFrom(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
        return "?";
    }
    if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function MedHeader({
    title,
    subLeft,
    subRight,
    progress
}: {
    title: string;
    subLeft: string;
    subRight: string;
    progress: number;
}) {
    return (
        <View style={styles.medHeader}>
            <Text style={styles.medHeaderTitle}>{title}</Text>
            <View style={styles.medHeaderSubRow}>
                <Text style={styles.medHeaderSubLeft}>{subLeft}</Text>
                <Text style={styles.medHeaderSubRight}>{subRight}</Text>
            </View>
            <View style={styles.progressTrack}>
                <View style={[styles.progressFill, { width: `${Math.round(progress * 100)}%` }]} />
            </View>
        </View>
    );
}

function MedCard({
    children,
    complete,
    error,
    cardRef,
    transform
}: {
    children: React.ReactNode;
    complete?: boolean;
    error?: boolean;
    cardRef?: React.Ref<View>;
    transform?: { translateX: Animated.Value };
}) {
    if (transform) {
        return (
            <Animated.View
                ref={cardRef as React.ComponentProps<typeof Animated.View>["ref"]}
                style={[
                    styles.medCard,
                    complete && styles.medCardComplete,
                    error && styles.medCardError,
                    { transform: [{ translateX: transform.translateX }] }
                ]}
            >
                {children}
            </Animated.View>
        );
    }
    return (
        <View
            ref={cardRef}
            style={[
                styles.medCard,
                complete && styles.medCardComplete,
                error && styles.medCardError
            ]}
        >
            {children}
        </View>
    );
}

type ChipVariant = "default" | "icd" | "needs" | "na" | "viewmore" | "method";

interface MedChipProps {
    label: string;
    selected?: boolean;
    disabled?: boolean;
    variant?: ChipVariant;
    onPress: () => void;
}

const MedChip = React.memo(
    MedChipBase,
    (prev, next) =>
        prev.label === next.label &&
        prev.selected === next.selected &&
        prev.disabled === next.disabled &&
        prev.variant === next.variant
);

function MedChipBase({ label, selected, disabled, variant = "default", onPress }: MedChipProps) {
    return (
        <Pressable
            onPress={onPress}
            style={[
                styles.chip,
                variant === "icd" && styles.chipIcd,
                variant === "viewmore" && styles.chipViewMore,
                variant === "method" && styles.chipMethod,
                selected && variant === "icd" && styles.chipIcdSelected,
                selected && variant === "default" && styles.chipSelected,
                selected && variant === "needs" && styles.chipNeedsSelected,
                selected && variant === "na" && styles.chipNaSelected,
                selected && variant === "method" && styles.chipMethodSelected,
                disabled && styles.chipDisabled
            ]}
        >
            {selected ? <Text style={styles.chipCheck}>✓ </Text> : null}
            <Text
                style={[
                    styles.chipText,
                    variant === "icd" && !selected && styles.chipIcdText,
                    variant === "viewmore" && styles.chipViewMoreText,
                    selected && styles.chipTextSelected
                ]}
            >
                {label}
            </Text>
        </Pressable>
    );
}

function MedBadge({ label, tone }: { label: string; tone: "teal" | "amber" | "grey" }) {
    return (
        <View
            style={[
                styles.badge,
                tone === "teal" && styles.badgeTeal,
                tone === "amber" && styles.badgeAmber,
                tone === "grey" && styles.badgeGrey
            ]}
        >
            <Text
                style={[
                    styles.badgeText,
                    tone === "teal" && styles.badgeTealText,
                    tone === "amber" && styles.badgeAmberText,
                    tone === "grey" && styles.badgeGreyText
                ]}
            >
                {label}
            </Text>
        </View>
    );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
    return <Text style={styles.fieldLabel}>{children}</Text>;
}

function StickyBar({
    primaryLabel,
    onPrimary,
    disabled,
    secondaryDisabled,
    secondaryLabel,
    onSecondary
}: {
    primaryLabel: string;
    onPrimary: () => void;
    disabled?: boolean;
    secondaryDisabled?: boolean;
    secondaryLabel?: string;
    onSecondary?: () => void;
}) {
    const split = !!(secondaryLabel && onSecondary);
    return (
        <View style={styles.buttons}>
            {split && (
                <Pressable
                    onPress={onSecondary}
                    disabled={secondaryDisabled}
                    style={[styles.backButton, secondaryDisabled && styles.btnDisabled]}
                >
                    <Text style={styles.backButtonText}>{secondaryLabel}</Text>
                </Pressable>
            )}
            <Pressable
                onPress={onPrimary}
                disabled={disabled}
                style={split ? styles.nextButton : styles.nextButtonFull}
            >
                {disabled ? (
                    <View style={[styles.nextButtonInner, styles.nextButtonDisabledInner]}>
                        <Text style={[styles.nextButtonText, styles.nextButtonTextDisabled]}>
                            {primaryLabel}
                        </Text>
                    </View>
                ) : (
                    <LinearGradient
                        colors={[...gradients.brand]}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                        style={styles.nextButtonInner}
                    >
                        <Text style={styles.nextButtonText}>{primaryLabel}</Text>
                    </LinearGradient>
                )}
            </Pressable>
        </View>
    );
}

function ManualInput({
    value,
    onChangeText,
    placeholder,
    disabled
}: {
    value: string;
    onChangeText: (s: string) => void;
    placeholder?: string;
    disabled?: boolean;
}) {
    return (
        <RNTextInput
            value={value}
            onChangeText={onChangeText}
            placeholder={placeholder}
            placeholderTextColor={colors.textTertiary}
            multiline
            editable={!disabled}
            autoCorrect={false}
            autoCapitalize="sentences"
            spellCheck={false}
            style={styles.manualInput}
        />
    );
}

export function PerformanceEvaluationStep({
    visitId,
    scheduledAideName,
    scheduledAidePhone,
    onComplete,
    onBack,
    readOnly
}: Props) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [navigating, setNavigating] = useState(false);
    const [snacks, setSnacks] = useState<string[]>([]);
    const [currentSubStep, setCurrentSubStep] = useState<StepIndex>(0);
    const [keyboardHeight, setKeyboardHeight] = useState(0);
    const [barHeight, setBarHeight] = useState(INITIAL_STICKY_BAR_HEIGHT);
    const insets = useSafeAreaInsets();

    const [items, setItems] = useState<PerformanceEvaluationItem[]>([]);
    const [isAidePresent, setIsAidePresent] = useState<boolean | null>(null);
    const initialActualAideName = scheduledAideName?.trim() ? null : "";
    const [actualAideName, setActualAideName] = useState<string | null>(initialActualAideName);
    const [aideRenameMode, setAideRenameMode] = useState(!scheduledAideName?.trim());
    const [aideDraftName, setAideDraftName] = useState("");
    const [absenceReasonTags, setAbsenceReasonTags] = useState<string[]>([]);
    const [absenceReasonNote, setAbsenceReasonNote] = useState("");
    const [ratings, setRatings] = useState<Record<string, PerformanceEvaluationRatingInput>>({});
    const [generalComments, setGeneralComments] = useState("");
    const [administrativeComments, setAdministrativeComments] = useState("");
    const [supervisorComments, setSupervisorComments] = useState("");
    const [rnSignature, setRnSignature] = useState<string | null>(null);
    const [caregiverSignature, setCaregiverSignature] = useState<string | null>(null);
    const [sigModalVisible, setSigModalVisible] = useState<"rn" | "caregiver" | null>(null);

    const scrollRef = useRef<ScrollView>(null);
    const savedSnapshotRef = useRef<PerformanceServerSnapshot | null>(null);
    const actualAideNameRef = useRef<string | null>(actualAideName);
    actualAideNameRef.current = actualAideName;
    const mountedRef = useRef(true);
    const inflightPersistRef = useRef<Promise<{ ok: boolean; error?: string }> | null>(null);
    const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const autosaveSuspendedRef = useRef(false);
    const sigDirtyRef = useRef<{ rn: boolean; caregiver: boolean }>({
        rn: false,
        caregiver: false
    });
    const sigTargetRef = useRef<"rn" | "caregiver" | null>(null);
    const prevSubStepRef = useRef(currentSubStep);
    const subStepScrollRef = useRef<Record<number, number>>({});

    const grouped = useMemo(() => groupItems(items), [items]);
    const visibleItemsByKey = useMemo(
        () => new Map(items.map((item) => [keyFor(item), item])),
        [items]
    );

    const pushSnack = useCallback((msg: string) => {
        setSnacks((prev) => {
            const next = [...prev, msg];
            return next.length > 3 ? next.slice(next.length - 3) : next;
        });
    }, []);

    const setSnack = pushSnack;

    const currentRatings = useMemo(
        () => normalizeRatingsForCompare(Object.values(ratings)),
        [ratings]
    );

    const makeSnapshot = useCallback(
        (overrides: Partial<PerformanceServerSnapshot> = {}): PerformanceServerSnapshot => ({
            isAidePresent,
            actualAideName: normalizeStr(actualAideName),
            absenceReasonTags,
            absenceReasonNote: normalizeStr(absenceReasonNote),
            generalComments: normalizeStr(generalComments),
            administrativeComments: normalizeStr(administrativeComments),
            supervisorComments: normalizeStr(supervisorComments),
            ratings: currentRatings,
            rnSignatureData: rnSignature,
            caregiverSignatureData: caregiverSignature,
            ...overrides
        }),
        [
            isAidePresent,
            actualAideName,
            absenceReasonTags,
            absenceReasonNote,
            generalComments,
            administrativeComments,
            supervisorComments,
            currentRatings,
            rnSignature,
            caregiverSignature
        ]
    );

    const isDirty = useCallback(() => {
        const snap = savedSnapshotRef.current;
        if (!snap) {
            return false;
        }
        const live = makeSnapshot();
        if (snap.isAidePresent !== live.isAidePresent) {
            return true;
        }
        if (snap.actualAideName !== live.actualAideName) {
            return true;
        }
        if (!arraysEqual(snap.absenceReasonTags, live.absenceReasonTags)) {
            return true;
        }
        if (snap.absenceReasonNote !== live.absenceReasonNote) {
            return true;
        }
        if (snap.generalComments !== live.generalComments) {
            return true;
        }
        if (snap.administrativeComments !== live.administrativeComments) {
            return true;
        }
        if (snap.supervisorComments !== live.supervisorComments) {
            return true;
        }
        if (snap.rnSignatureData !== live.rnSignatureData) {
            return true;
        }
        if (snap.caregiverSignatureData !== live.caregiverSignatureData) {
            return true;
        }
        if (snap.ratings.length !== live.ratings.length) {
            return true;
        }
        for (let i = 0; i < snap.ratings.length; i++) {
            const a = snap.ratings[i];
            const b = live.ratings[i];
            if (
                a.sourceType !== b.sourceType ||
                a.sourceId !== b.sourceId ||
                a.rating !== b.rating ||
                a.note !== b.note ||
                !arraysEqual(a.methods, b.methods)
            ) {
                return true;
            }
        }
        return false;
    }, [makeSnapshot]);

    const isDirtyRef = useRef(isDirty);
    isDirtyRef.current = isDirty;

    useEffect(() => {
        return () => {
            mountedRef.current = false;
            if (autosaveTimerRef.current) {
                clearTimeout(autosaveTimerRef.current);
                autosaveTimerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        async function load() {
            setLoading(true);
            try {
                const [itemRes, saved] = await Promise.all([
                    fetchPerformanceEvaluationItems(visitId),
                    fetchPerformanceEvaluation(visitId)
                ]);
                if (cancelled) {
                    return;
                }

                const nextItems = itemRes.items;
                const nextGrouped = groupItems(nextItems);
                const nextVisibleItemsByKey = new Map(
                    nextItems.map((item) => [keyFor(item), item])
                );
                const nextRatings: Record<string, PerformanceEvaluationRatingInput> = {};
                for (const rating of saved.ratings ?? []) {
                    const sanitized = sanitizeRating(rating, nextVisibleItemsByKey);
                    if (sanitized) {
                        nextRatings[keyForRating(sanitized)] = sanitized;
                    }
                }

                const snapshot: PerformanceServerSnapshot = {
                    isAidePresent: saved.evaluation?.isAidePresent ?? null,
                    actualAideName: normalizeStr(
                        saved.evaluation?.actualAideName ?? scheduledAideName ?? ""
                    ),
                    absenceReasonTags: saved.evaluation?.absenceReasonTags ?? [],
                    absenceReasonNote: normalizeStr(saved.evaluation?.absenceReasonNote),
                    generalComments: normalizeStr(saved.evaluation?.generalComments),
                    administrativeComments: normalizeStr(saved.evaluation?.administrativeComments),
                    supervisorComments: normalizeStr(saved.evaluation?.supervisorComments),
                    ratings: normalizeRatingsForCompare(Object.values(nextRatings)),
                    rnSignatureData: saved.rnSignatureData ?? null,
                    caregiverSignatureData: saved.caregiverSignatureData ?? null
                };

                setItems(nextItems);
                setIsAidePresent(snapshot.isAidePresent);
                if (snapshot.actualAideName) {
                    setActualAideName(snapshot.actualAideName);
                    actualAideNameRef.current = snapshot.actualAideName;
                    setAideRenameMode(false);
                } else {
                    setActualAideName(scheduledAideName?.trim() ? null : "");
                    actualAideNameRef.current = scheduledAideName?.trim() ? null : "";
                    setAideRenameMode(!scheduledAideName?.trim());
                }
                setAbsenceReasonTags(snapshot.absenceReasonTags);
                setAbsenceReasonNote(snapshot.absenceReasonNote);
                setGeneralComments(snapshot.generalComments);
                setAdministrativeComments(snapshot.administrativeComments);
                setSupervisorComments(snapshot.supervisorComments);
                setRnSignature(snapshot.rnSignatureData);
                setCaregiverSignature(snapshot.caregiverSignatureData);
                setRatings(nextRatings);
                savedSnapshotRef.current = snapshot;
                autosaveSuspendedRef.current = false;
                setCurrentSubStep(computeResumeSubStep(snapshot, nextGrouped, scheduledAideName));
            } catch (e) {
                if (!cancelled) {
                    if (e instanceof ApiError && e.status === 401) {
                        autosaveSuspendedRef.current = true;
                    }
                    setSnack(e instanceof Error ? e.message : "Failed to load evaluation");
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        load();
        return () => {
            cancelled = true;
        };
    }, [scheduledAideName, setSnack, visitId]);

    const persist = useCallback(
        (opts: { isComplete?: boolean } = {}): Promise<{ ok: boolean; error?: string }> => {
            if (readOnly) {
                return Promise.resolve({ ok: true });
            }
            const actualSaveBody = async (): Promise<{ ok: boolean; error?: string }> => {
                if (mountedRef.current) {
                    setSaving(true);
                }
                const sentActualAideName = normalizeStr(actualAideNameRef.current);
                const sentAbsenceReasonTags = absenceReasonTags;
                const sentAbsenceReasonNote = normalizeStr(absenceReasonNote);
                const sentGeneralComments = normalizeStr(generalComments);
                const sentAdministrativeComments = normalizeStr(administrativeComments);
                const sentSupervisorComments = normalizeStr(supervisorComments);
                const sentIsAidePresent = isAidePresent === true;
                const sentRatings = Object.values(ratings)
                    .map((rating) => sanitizeRating(rating, visibleItemsByKey))
                    .filter(
                        (rating): rating is PerformanceEvaluationRatingInput => rating !== null
                    );
                const sigRnSent = sigDirtyRef.current.rn;
                const sigCaregiverSent = sigDirtyRef.current.caregiver;
                try {
                    await savePerformanceEvaluation(visitId, {
                        isAidePresent: sentIsAidePresent,
                        actualAideName: sentActualAideName,
                        absenceReasonTags: sentAbsenceReasonTags,
                        absenceReasonNote: sentAbsenceReasonNote,
                        generalComments: sentGeneralComments,
                        administrativeComments: sentAdministrativeComments,
                        supervisorComments: sentSupervisorComments,
                        ratings: sentIsAidePresent ? sentRatings : [],
                        rnSignatureData: sigRnSent ? (rnSignature ?? undefined) : undefined,
                        caregiverSignatureData: sigCaregiverSent
                            ? (caregiverSignature ?? undefined)
                            : undefined,
                        caregiverSignerName: sentActualAideName,
                        isComplete: opts.isComplete
                    });
                    autosaveSuspendedRef.current = false;
                    if (sigRnSent) {
                        sigDirtyRef.current.rn = false;
                    }
                    if (sigCaregiverSent) {
                        sigDirtyRef.current.caregiver = false;
                    }
                    savedSnapshotRef.current = makeSnapshot({
                        isAidePresent: sentIsAidePresent,
                        actualAideName: sentActualAideName,
                        absenceReasonTags: sentAbsenceReasonTags,
                        absenceReasonNote: sentAbsenceReasonNote,
                        generalComments: sentGeneralComments,
                        administrativeComments: sentAdministrativeComments,
                        supervisorComments: sentSupervisorComments,
                        ratings: normalizeRatingsForCompare(sentIsAidePresent ? sentRatings : []),
                        rnSignatureData: sigRnSent
                            ? rnSignature
                            : (savedSnapshotRef.current?.rnSignatureData ?? null),
                        caregiverSignatureData: sigCaregiverSent
                            ? caregiverSignature
                            : (savedSnapshotRef.current?.caregiverSignatureData ?? null)
                    });
                    return { ok: true };
                } catch (e) {
                    if (e instanceof ApiError && e.status === 401) {
                        autosaveSuspendedRef.current = true;
                    }
                    return { ok: false, error: e instanceof Error ? e.message : "Save failed" };
                } finally {
                    if (mountedRef.current) {
                        setSaving(false);
                    }
                }
            };
            const previous = inflightPersistRef.current ?? Promise.resolve({ ok: true });
            const promise = previous.catch(() => ({ ok: false })).then(() => actualSaveBody());
            inflightPersistRef.current = promise;
            return promise.finally(() => {
                if (inflightPersistRef.current === promise) {
                    inflightPersistRef.current = null;
                }
            });
        },
        [
            readOnly,
            ratings,
            visitId,
            isAidePresent,
            absenceReasonTags,
            absenceReasonNote,
            generalComments,
            administrativeComments,
            supervisorComments,
            rnSignature,
            caregiverSignature,
            makeSnapshot,
            visibleItemsByKey
        ]
    );

    const persistRef = useRef(persist);
    persistRef.current = persist;

    useEffect(() => {
        return () => {
            if (isDirtyRef.current()) {
                void persistRef.current();
            }
        };
    }, []);

    useEffect(() => {
        const prev = prevSubStepRef.current;
        if (currentSubStep > prev) {
            scrollRef.current?.scrollTo({ y: 0, animated: false });
        } else if (currentSubStep < prev) {
            const cached = subStepScrollRef.current[currentSubStep] ?? 0;
            scrollRef.current?.scrollTo({ y: cached, animated: false });
        }
        prevSubStepRef.current = currentSubStep;
    }, [currentSubStep]);

    useEffect(() => {
        const showEvent = Platform.OS === "ios" ? "keyboardWillShow" : "keyboardDidShow";
        const hideEvent = Platform.OS === "ios" ? "keyboardWillHide" : "keyboardDidHide";
        const showSub = Keyboard.addListener(showEvent, (e) =>
            setKeyboardHeight(e.endCoordinates.height)
        );
        const hideSub = Keyboard.addListener(hideEvent, () => setKeyboardHeight(0));
        return () => {
            showSub.remove();
            hideSub.remove();
        };
    }, []);

    useEffect(() => {
        if (
            loading ||
            readOnly ||
            savedSnapshotRef.current === null ||
            autosaveSuspendedRef.current
        ) {
            return;
        }
        if (autosaveTimerRef.current) {
            clearTimeout(autosaveTimerRef.current);
        }
        autosaveTimerRef.current = setTimeout(() => {
            if (inflightPersistRef.current || !isDirtyRef.current()) {
                return;
            }
            void persist();
        }, AUTOSAVE_DEBOUNCE_MS);
        return () => {
            if (autosaveTimerRef.current) {
                clearTimeout(autosaveTimerRef.current);
                autosaveTimerRef.current = null;
            }
        };
    }, [
        loading,
        readOnly,
        isAidePresent,
        actualAideName,
        absenceReasonTags,
        absenceReasonNote,
        ratings,
        generalComments,
        administrativeComments,
        supervisorComments,
        rnSignature,
        caregiverSignature,
        persist
    ]);

    const toggleAbsenceReason = useCallback((reason: string) => {
        setAbsenceReasonTags((cur) =>
            cur.includes(reason) ? cur.filter((item) => item !== reason) : [reason]
        );
    }, []);

    const updateRating = useCallback(
        (item: PerformanceEvaluationItem, patch: Partial<PerformanceEvaluationRatingInput>) => {
            const key = keyFor(item);
            setRatings((cur) => ({
                ...cur,
                [key]: {
                    sourceType: item.sourceType,
                    sourceId: item.sourceId,
                    itemKey: item.itemKey,
                    category: item.category,
                    label: item.label,
                    order: item.sortOrder,
                    rating: cur[key]?.rating ?? "competent",
                    methods: cur[key]?.methods ?? [],
                    note: cur[key]?.note ?? "",
                    ...patch
                }
            }));
        },
        []
    );

    const toggleMethod = useCallback(
        (item: PerformanceEvaluationItem, method: Method) => {
            const current = ratings[keyFor(item)]?.methods ?? [];
            updateRating(item, {
                methods: current.includes(method)
                    ? current.filter((value) => value !== method)
                    : [...current, method]
            });
        },
        [ratings, updateRating]
    );

    const markAll = useCallback((rows: PerformanceEvaluationItem[]) => {
        setRatings((cur) => {
            const next = { ...cur };
            for (const item of rows) {
                const key = keyFor(item);
                next[key] = {
                    sourceType: item.sourceType,
                    sourceId: item.sourceId,
                    itemKey: item.itemKey,
                    category: item.category,
                    label: item.label,
                    order: item.sortOrder,
                    rating: "competent",
                    methods: cur[key]?.methods?.length ? cur[key].methods : ["observation"],
                    note: cur[key]?.note ?? ""
                };
            }
            return next;
        });
    }, []);

    function validateStep(step: StepIndex): boolean {
        if (step === 0) {
            const aideName = normalizeStr(actualAideName ?? scheduledAideName ?? "");
            if (!aideName) {
                setSnack("Enter the aide's name to continue.");
                return false;
            }
            if (isAidePresent === null) {
                setSnack("Pick Yes or No to continue.");
                return false;
            }
            if (
                isAidePresent === false &&
                absenceReasonTags.length === 0 &&
                !absenceReasonNote.trim()
            ) {
                setSnack("Tell us why the aide isn't here.");
                return false;
            }
        }
        if (step === 1 && !validateRows(grouped.general)) {
            return false;
        }
        if (step === 2 && !validateRows(grouped.administrative)) {
            return false;
        }
        return true;
    }

    function validateRows(rows: PerformanceEvaluationItem[]): boolean {
        if (isAidePresent === false) {
            return true;
        }
        for (const item of rows) {
            const rating = ratings[keyFor(item)];
            if (!rating) {
                setSnack(`Rate ${item.label}.`);
                return false;
            }
            if (rating.methods.length === 0) {
                setSnack(`Select Verbal, Observation, or both for ${item.label}.`);
                return false;
            }
            if (rating.rating === "needs_improvement" && !rating.note?.trim()) {
                setSnack(`Needs Improvement requires a note for ${item.label}.`);
                return false;
            }
        }
        return true;
    }

    const handleNext = useCallback(async () => {
        if (readOnly || navigating) {
            return;
        }
        setNavigating(true);

        if (currentSubStep === 0 && aideRenameMode && aideDraftName.trim() && !actualAideName) {
            const trimmed = aideDraftName.trim();
            setActualAideName(trimmed);
            actualAideNameRef.current = trimmed;
            setAideRenameMode(false);
            setAideDraftName("");
        }

        if (!validateStep(currentSubStep)) {
            setNavigating(false);
            return;
        }

        if (autosaveTimerRef.current) {
            clearTimeout(autosaveTimerRef.current);
            autosaveTimerRef.current = null;
        }
        const result = await persist();
        if (!mountedRef.current) {
            return;
        }
        if (!result.ok) {
            setSnack(result.error ?? "Save failed - please retry");
            setNavigating(false);
            return;
        }
        setCurrentSubStep((step) => Math.min(step + 1, SUB_STEPS.length - 1) as StepIndex);
        setNavigating(false);
    }, [
        aideDraftName,
        aideRenameMode,
        actualAideName,
        currentSubStep,
        navigating,
        persist,
        readOnly
    ]);

    const handleBack = useCallback(async () => {
        if (readOnly || navigating) {
            return;
        }
        setNavigating(true);
        if (autosaveTimerRef.current) {
            clearTimeout(autosaveTimerRef.current);
            autosaveTimerRef.current = null;
        }
        await persist();
        if (!mountedRef.current) {
            return;
        }
        if (currentSubStep > 0) {
            setCurrentSubStep((step) => Math.max(step - 1, 0) as StepIndex);
            setNavigating(false);
        } else {
            onBack();
        }
    }, [currentSubStep, navigating, onBack, persist, readOnly]);

    const handleComplete = useCallback(async () => {
        if (!rnSignature) {
            setSnack("RN signature is required");
            return;
        }
        if (isAidePresent !== false && !caregiverSignature) {
            setSnack("Aide signature is required");
            return;
        }
        setNavigating(true);
        if (autosaveTimerRef.current) {
            clearTimeout(autosaveTimerRef.current);
            autosaveTimerRef.current = null;
        }
        const result = await persist({ isComplete: true });
        if (!mountedRef.current) {
            return;
        }
        setNavigating(false);
        if (!result.ok) {
            setSnack(result.error ?? "Save failed - please retry");
            return;
        }
        onComplete();
    }, [caregiverSignature, isAidePresent, onComplete, persist, rnSignature]);

    function renderAidePresence() {
        const aideName = actualAideName ?? scheduledAideName ?? "";
        const isRenamed = actualAideName !== null && actualAideName.trim().length > 0;
        const firstName = aideName.split(/\s+/)[0] || "the aide";

        const commitDraft = () => {
            const trimmed = aideDraftName.trim();
            if (!trimmed) {
                return;
            }
            setActualAideName(trimmed);
            actualAideNameRef.current = trimmed;
            setAideRenameMode(false);
            setAideDraftName("");
        };
        const cancelDraft = () => {
            if (!scheduledAideName?.trim() && !isRenamed) {
                return;
            }
            setAideRenameMode(false);
            setAideDraftName("");
        };

        return (
            <View>
                <View style={styles.identityCard}>
                    <View style={styles.identityOrbA} />
                    <View style={styles.identityOrbB} />

                    {aideRenameMode ? (
                        <View>
                            <Text style={styles.identityEyebrow}>WHO'S THE AIDE TODAY?</Text>
                            <View style={styles.identityInputPill}>
                                <MaterialCommunityIcons
                                    name="account-outline"
                                    size={18}
                                    color={colors.gray500}
                                />
                                <RNTextInput
                                    value={aideDraftName}
                                    onChangeText={setAideDraftName}
                                    placeholder="Type full name..."
                                    placeholderTextColor={colors.textTertiary}
                                    autoFocus
                                    style={styles.identityInputText}
                                    editable={!readOnly}
                                    onSubmitEditing={commitDraft}
                                    returnKeyType="done"
                                    autoCorrect={false}
                                    autoCapitalize="words"
                                    spellCheck={false}
                                />
                            </View>
                            <View style={styles.identityActionRow}>
                                <Pressable
                                    onPress={cancelDraft}
                                    style={[
                                        styles.identityActionBtn,
                                        styles.identityActionBtnGhost
                                    ]}
                                >
                                    <Text style={styles.identityActionBtnGhostText}>Cancel</Text>
                                </Pressable>
                                <Pressable
                                    onPress={commitDraft}
                                    disabled={!aideDraftName.trim()}
                                    style={[
                                        styles.identityActionBtn,
                                        styles.identityActionBtnSolid,
                                        !aideDraftName.trim() && styles.identityActionBtnDisabled
                                    ]}
                                >
                                    <Text style={styles.identityActionBtnSolidText}>
                                        Use this name
                                    </Text>
                                </Pressable>
                            </View>
                        </View>
                    ) : (
                        <View style={styles.identityRow}>
                            <View style={styles.identityAvatar}>
                                <Text style={styles.identityAvatarText}>
                                    {initialsFrom(aideName)}
                                </Text>
                            </View>
                            <View style={styles.identityNameCol}>
                                <Text style={styles.identityEyebrow}>
                                    {isRenamed ? "AIDE ON EVALUATION" : "SCHEDULED AIDE"}
                                </Text>
                                <Text style={styles.identityName} numberOfLines={1}>
                                    {aideName}
                                </Text>
                                {/* The scheduled aide's number belongs to the
                                    scheduled aide — shown only while the card
                                    still names her. See showScheduledAidePhone. */}
                                {showScheduledAidePhone(
                                    aideName,
                                    scheduledAideName,
                                    scheduledAidePhone
                                ) ? (
                                    <Text style={styles.identityPhone} selectable>
                                        {formatAidePhone(scheduledAidePhone ?? "")}
                                    </Text>
                                ) : null}
                                {!readOnly && (
                                    <Pressable
                                        onPress={() => {
                                            setAideDraftName(actualAideName ?? "");
                                            setAideRenameMode(true);
                                        }}
                                        hitSlop={6}
                                    >
                                        <Text style={styles.identityLink}>
                                            {isRenamed
                                                ? "Edit name"
                                                : `Not ${firstName}? Tap to change`}
                                        </Text>
                                    </Pressable>
                                )}
                            </View>
                        </View>
                    )}
                </View>

                <Text style={styles.heroQuestion}>Is the aide present during this evaluation?</Text>
                <Text style={styles.heroHint}>
                    If the aide is absent, document the reason and the RN can sign off from here.
                </Text>

                <View style={styles.choiceRow}>
                    <Pressable
                        onPress={() => !readOnly && setIsAidePresent(true)}
                        disabled={readOnly}
                        style={styles.choiceCardWrap}
                    >
                        {isAidePresent === true ? (
                            <LinearGradient
                                colors={[colors.teal400, colors.teal500]}
                                start={{ x: 0, y: 0 }}
                                end={{ x: 1, y: 1 }}
                                style={[styles.choiceCard, styles.choiceCardYesSelected]}
                            >
                                <View style={styles.choiceOrbYesSelected} />
                                <View style={styles.choiceContent}>
                                    <View style={styles.choiceIconYesSelected}>
                                        <MaterialCommunityIcons
                                            name="check"
                                            size={20}
                                            color={colors.surface}
                                        />
                                    </View>
                                    <Text style={[styles.choiceTitle, styles.choiceTitleSelected]}>
                                        Yes
                                    </Text>
                                    <Text style={[styles.choiceSub, styles.choiceSubYesSelected]}>
                                        Aide is here
                                    </Text>
                                </View>
                            </LinearGradient>
                        ) : (
                            <View style={[styles.choiceCard, styles.choiceCardUnselected]}>
                                <View style={styles.choiceOrbYesUnselected} />
                                <View style={styles.choiceContent}>
                                    <View style={styles.choiceIconYesUnselected}>
                                        <MaterialCommunityIcons
                                            name="check"
                                            size={20}
                                            color={colors.teal500}
                                        />
                                    </View>
                                    <Text style={styles.choiceTitle}>Yes</Text>
                                    <Text style={styles.choiceSub}>Aide is here</Text>
                                </View>
                            </View>
                        )}
                    </Pressable>
                    <Pressable
                        onPress={() => !readOnly && setIsAidePresent(false)}
                        disabled={readOnly}
                        style={styles.choiceCardWrap}
                    >
                        <View
                            style={[
                                styles.choiceCard,
                                isAidePresent === false
                                    ? styles.choiceCardNoSelected
                                    : styles.choiceCardUnselected
                            ]}
                        >
                            <View
                                style={
                                    isAidePresent === false
                                        ? styles.choiceOrbNoSelected
                                        : styles.choiceOrbNoUnselected
                                }
                            />
                            <View style={styles.choiceContent}>
                                <View
                                    style={
                                        isAidePresent === false
                                            ? styles.choiceIconNoSelected
                                            : styles.choiceIconNoUnselected
                                    }
                                >
                                    <MaterialCommunityIcons
                                        name="close"
                                        size={20}
                                        color={
                                            isAidePresent === false
                                                ? colors.warning600
                                                : colors.gray500
                                        }
                                    />
                                </View>
                                <Text style={styles.choiceTitle}>No</Text>
                                <Text
                                    style={[
                                        styles.choiceSub,
                                        isAidePresent === false && styles.choiceSubNoSelected
                                    ]}
                                >
                                    {isAidePresent === false ? "Tell us why" : "Skip duty ratings"}
                                </Text>
                            </View>
                        </View>
                    </Pressable>
                </View>

                {isAidePresent === false && (
                    <View style={styles.reasonBlock}>
                        <Text style={styles.reasonLabel}>
                            WHY ISN'T THE AIDE HERE?
                            <Text style={styles.reasonLabelRequired}> · required</Text>
                        </Text>
                        <View style={styles.reasonChipRow}>
                            {ABSENCE_REASONS.map((reason) => {
                                const selected = absenceReasonTags.includes(reason);
                                return (
                                    <Pressable
                                        key={reason}
                                        onPress={() => !readOnly && toggleAbsenceReason(reason)}
                                        style={[
                                            styles.reasonChip,
                                            selected && styles.reasonChipSelected
                                        ]}
                                    >
                                        {selected && <Text style={styles.reasonChipCheck}>✓ </Text>}
                                        <Text
                                            style={[
                                                styles.reasonChipText,
                                                selected && styles.reasonChipTextSelected
                                            ]}
                                        >
                                            {reason}
                                        </Text>
                                    </Pressable>
                                );
                            })}
                        </View>
                        <RNTextInput
                            value={absenceReasonNote}
                            onChangeText={setAbsenceReasonNote}
                            placeholder="Add details for the patient record..."
                            placeholderTextColor={colors.textTertiary}
                            multiline
                            editable={!readOnly}
                            style={styles.reasonNote}
                        />
                    </View>
                )}

                {isAidePresent === false && renderRnSignatureOnly()}
            </View>
        );
    }

    function renderRnSignatureOnly() {
        return (
            <MedCard complete={!!rnSignature}>
                <View style={styles.cardHead}>
                    <Text style={styles.cardCatTitle}>RN Signature</Text>
                    <MedBadge
                        label={rnSignature ? "Signed" : "Pending"}
                        tone={rnSignature ? "teal" : "amber"}
                    />
                </View>
                {rnSignature ? (
                    <Image
                        {...logRocketRedactProps()}
                        source={{ uri: rnSignature }}
                        style={styles.sigImage}
                        resizeMode="contain"
                    />
                ) : (
                    <Pressable
                        onPress={() => {
                            if (readOnly) {
                                return;
                            }
                            sigTargetRef.current = "rn";
                            setSigModalVisible("rn");
                        }}
                        style={styles.sigPad}
                    >
                        <Text style={styles.sigPadText}>Tap to sign</Text>
                    </Pressable>
                )}
            </MedCard>
        );
    }

    function renderDutyList(title: string, rows: PerformanceEvaluationItem[]) {
        const completeCount = rows.filter((item) => {
            const rating = ratings[keyFor(item)];
            return (
                !!rating &&
                rating.methods.length > 0 &&
                (rating.rating !== "needs_improvement" || !!rating.note?.trim())
            );
        }).length;
        const allComplete = rows.length > 0 && completeCount === rows.length;
        return (
            <MedCard complete={allComplete}>
                <View style={styles.cardHead}>
                    <Text style={styles.cardCatTitle}>{title}</Text>
                    {allComplete ? (
                        <MedBadge label="All rated" tone="teal" />
                    ) : (
                        <Pressable
                            onPress={() => !readOnly && markAll(rows)}
                            style={styles.markAllPill}
                        >
                            <Text style={styles.markAllPillText}>Mark all</Text>
                        </Pressable>
                    )}
                </View>
                <Text style={styles.simpleIntro}>
                    Rate each duty and mark whether the evaluation was verbal, observed, or both.
                </Text>
                {rows.map((item) => {
                    const rating = ratings[keyFor(item)];
                    return (
                        <View key={item.id} style={styles.rateRow}>
                            <View style={styles.rateRowNameRow}>
                                <Text style={styles.rateRowName}>{item.label}</Text>
                            </View>
                            <View style={styles.chipRow}>
                                <MedChip
                                    label="Competent"
                                    selected={rating?.rating === "competent"}
                                    onPress={() =>
                                        !readOnly && updateRating(item, { rating: "competent" })
                                    }
                                />
                                <MedChip
                                    label="Needs Impr."
                                    selected={rating?.rating === "needs_improvement"}
                                    variant="needs"
                                    onPress={() =>
                                        !readOnly &&
                                        updateRating(item, { rating: "needs_improvement" })
                                    }
                                />
                            </View>
                            <View style={styles.methodRow}>
                                <MedChip
                                    label="Verbal"
                                    selected={rating?.methods.includes("verbal")}
                                    variant="method"
                                    onPress={() => !readOnly && toggleMethod(item, "verbal")}
                                />
                                <MedChip
                                    label="Observation"
                                    selected={rating?.methods.includes("observation")}
                                    variant="method"
                                    onPress={() => !readOnly && toggleMethod(item, "observation")}
                                />
                            </View>
                            {rating?.rating === "needs_improvement" && (
                                <ManualInput
                                    value={rating.note ?? ""}
                                    onChangeText={(note) => updateRating(item, { note })}
                                    placeholder="Describe what needs improvement..."
                                    disabled={readOnly}
                                />
                            )}
                        </View>
                    );
                })}
            </MedCard>
        );
    }

    function renderComments() {
        return (
            <MedCard>
                <FieldLabel>General comments</FieldLabel>
                <ManualInput
                    value={generalComments}
                    onChangeText={setGeneralComments}
                    placeholder="General field evaluation comments..."
                    disabled={readOnly}
                />
                <FieldLabel>Administrative comments</FieldLabel>
                <ManualInput
                    value={administrativeComments}
                    onChangeText={setAdministrativeComments}
                    placeholder="Administrative performance comments..."
                    disabled={readOnly}
                />
                <FieldLabel>Supervisor comments</FieldLabel>
                <ManualInput
                    value={supervisorComments}
                    onChangeText={setSupervisorComments}
                    placeholder="Recommendations or follow-up actions..."
                    disabled={readOnly}
                />
            </MedCard>
        );
    }

    function renderSignatures() {
        return (
            <>
                <MedCard complete={!!rnSignature}>
                    <View style={styles.cardHead}>
                        <Text style={styles.cardCatTitle}>RN Signature</Text>
                        <MedBadge
                            label={rnSignature ? "Signed" : "Pending"}
                            tone={rnSignature ? "teal" : "amber"}
                        />
                    </View>
                    {rnSignature ? (
                        <Image
                            {...logRocketRedactProps()}
                            source={{ uri: rnSignature }}
                            style={styles.sigImage}
                            resizeMode="contain"
                        />
                    ) : (
                        <Pressable
                            onPress={() => {
                                if (readOnly) {
                                    return;
                                }
                                sigTargetRef.current = "rn";
                                setSigModalVisible("rn");
                            }}
                            style={styles.sigPad}
                        >
                            <Text style={styles.sigPadText}>Tap to sign</Text>
                        </Pressable>
                    )}
                </MedCard>

                <MedCard complete={!!caregiverSignature}>
                    <View style={styles.cardHead}>
                        <Text style={styles.cardCatTitle}>Aide Signature</Text>
                        <MedBadge
                            label={caregiverSignature ? "Signed" : "Pending"}
                            tone={caregiverSignature ? "teal" : "amber"}
                        />
                    </View>
                    {caregiverSignature ? (
                        <Image
                            {...logRocketRedactProps()}
                            source={{ uri: caregiverSignature }}
                            style={styles.sigImage}
                            resizeMode="contain"
                        />
                    ) : (
                        <Pressable
                            onPress={() => {
                                if (readOnly) {
                                    return;
                                }
                                sigTargetRef.current = "caregiver";
                                setSigModalVisible("caregiver");
                            }}
                            style={styles.sigPad}
                        >
                            <Text style={styles.sigPadText}>Tap to sign</Text>
                        </Pressable>
                    )}
                </MedCard>
            </>
        );
    }

    if (loading) {
        return (
            <View style={styles.centerContainer}>
                <ActivityIndicator size="large" color={colors.teal400} />
                <Text style={styles.loadingText}>Loading performance evaluation...</Text>
            </View>
        );
    }

    const absentTerminal = isAidePresent === false;
    const completeReady = absentTerminal ? !!rnSignature : !!rnSignature && !!caregiverSignature;
    const showComplete =
        currentSubStep === SUB_STEPS.length - 1 || (absentTerminal && currentSubStep === 0);

    return (
        <View style={styles.container}>
            <MedHeader
                title="Aide Performance Evaluation"
                subLeft={SUB_STEPS[currentSubStep]}
                subRight={`${currentSubStep + 1} of ${SUB_STEPS.length}`}
                progress={SUB_STEPS.length > 1 ? currentSubStep / (SUB_STEPS.length - 1) : 1}
            />

            <ScrollView
                ref={scrollRef}
                style={styles.body}
                contentContainerStyle={[
                    styles.bodyContent,
                    {
                        paddingBottom:
                            barHeight + (keyboardHeight > 0 ? keyboardHeight : insets.bottom) + 16
                    }
                ]}
                onScroll={(e) => {
                    subStepScrollRef.current[currentSubStep] = e.nativeEvent.contentOffset.y;
                }}
                scrollEventThrottle={64}
                keyboardShouldPersistTaps="handled"
                keyboardDismissMode={Platform.OS === "ios" ? "interactive" : "none"}
            >
                {currentSubStep === 0 && renderAidePresence()}
                {currentSubStep === 1 &&
                    renderDutyList("General / Field Evaluation", grouped.general)}
                {currentSubStep === 2 &&
                    renderDutyList("Administrative Performance", grouped.administrative)}
                {currentSubStep === 3 && renderComments()}
                {currentSubStep === 4 && renderSignatures()}
            </ScrollView>

            {!readOnly && (
                <View
                    pointerEvents="box-none"
                    onLayout={(e) => setBarHeight(e.nativeEvent.layout.height)}
                    style={[
                        styles.stickyWrap,
                        { bottom: keyboardHeight > 0 ? keyboardHeight : insets.bottom }
                    ]}
                >
                    <StickyBar
                        primaryLabel={
                            showComplete
                                ? completeReady
                                    ? "Complete Evaluation"
                                    : "Complete Evaluation · sign to enable"
                                : "Continue"
                        }
                        onPrimary={showComplete ? handleComplete : handleNext}
                        disabled={navigating || (showComplete && !completeReady)}
                        secondaryDisabled={navigating}
                        secondaryLabel="Back"
                        onSecondary={handleBack}
                    />
                </View>
            )}

            <SignatureModal
                key={sigModalVisible}
                visible={sigModalVisible !== null}
                title={sigModalVisible === "rn" ? "RN Signature" : "Aide Signature"}
                onDone={(base64) => {
                    const target = sigTargetRef.current;
                    if (target === "rn") {
                        setRnSignature(base64);
                        sigDirtyRef.current.rn = true;
                    } else if (target === "caregiver") {
                        setCaregiverSignature(base64);
                        sigDirtyRef.current.caregiver = true;
                    }
                    sigTargetRef.current = null;
                    setSigModalVisible(null);
                }}
                onClose={() => {
                    sigTargetRef.current = null;
                    setSigModalVisible(null);
                }}
            />

            <Snackbar
                visible={snacks.length > 0}
                onDismiss={() => setSnacks((prev) => prev.slice(1))}
                duration={2000}
                style={styles.snackbar}
                wrapperStyle={{
                    bottom: barHeight + (keyboardHeight > 0 ? keyboardHeight : insets.bottom) + 8
                }}
            >
                {snacks[0] ?? ""}
            </Snackbar>
        </View>
    );
}

const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.gray50 },
    centerContainer: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        padding: 32
    },
    loadingText: {
        marginTop: 16,
        fontSize: 16,
        fontWeight: "600",
        color: colors.textPrimary
    },

    medHeader: {
        backgroundColor: colors.surface,
        paddingHorizontal: 20,
        paddingTop: 8,
        paddingBottom: 16,
        borderBottomWidth: 1,
        borderBottomColor: colors.border
    },
    medHeaderTitle: {
        fontSize: 22,
        fontWeight: "800",
        color: colors.textPrimary,
        letterSpacing: -0.2
    },
    medHeaderSubRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: 8
    },
    medHeaderSubLeft: { fontSize: 12, fontWeight: "500", color: colors.textSecondary },
    medHeaderSubRight: { fontSize: 12, fontWeight: "700", color: colors.teal400 },
    progressTrack: {
        height: 4,
        borderRadius: 9999,
        backgroundColor: colors.gray200,
        marginTop: 8,
        overflow: "hidden"
    },
    progressFill: {
        height: "100%",
        borderRadius: 9999,
        backgroundColor: colors.teal400
    },

    body: { flex: 1 },
    stickyWrap: { position: "absolute", left: 0, right: 0 },
    bodyContent: { padding: 16, paddingBottom: 32 },

    heroQuestion: {
        fontSize: 22,
        fontWeight: "800",
        color: colors.textPrimary,
        letterSpacing: -0.3,
        lineHeight: 28,
        marginBottom: 4
    },
    heroHint: {
        fontSize: 13,
        fontWeight: "500",
        color: colors.textSecondary,
        marginBottom: 14,
        lineHeight: 19
    },

    // ── Identity hero card ─────────────────────────────────
    identityCard: {
        borderRadius: 22,
        overflow: "hidden",
        marginBottom: 16,
        backgroundColor: colors.teal400,
        padding: 20,
        position: "relative",
        // Brand-tinted shadow
        shadowColor: colors.teal500,
        shadowOffset: { width: 0, height: 12 },
        shadowOpacity: 0.3,
        shadowRadius: 30,
        elevation: 8
    },
    identityOrbA: {
        position: "absolute",
        top: -40,
        right: -40,
        width: 160,
        height: 160,
        borderRadius: 999,
        backgroundColor: "rgba(255,255,255,0.10)"
    },
    identityOrbB: {
        position: "absolute",
        bottom: -50,
        left: -20,
        width: 120,
        height: 120,
        borderRadius: 999,
        backgroundColor: "rgba(255,255,255,0.08)"
    },
    identityRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 14
    },
    identityAvatar: {
        width: 60,
        height: 60,
        borderRadius: 999,
        backgroundColor: "rgba(255,255,255,0.25)",
        borderWidth: 2,
        borderColor: "rgba(255,255,255,0.5)",
        alignItems: "center",
        justifyContent: "center"
    },
    identityAvatarText: {
        color: colors.surface,
        fontSize: 20,
        fontWeight: "800"
    },
    identityNameCol: {
        flex: 1,
        minWidth: 0
    },
    identityEyebrow: {
        fontSize: 11,
        fontWeight: "700",
        letterSpacing: 0.6,
        color: colors.surface,
        opacity: 0.9,
        textTransform: "uppercase",
        marginBottom: 4
    },
    identityName: {
        fontSize: 20,
        fontWeight: "800",
        color: colors.surface,
        lineHeight: 24
    },
    identityPhone: {
        marginTop: 4,
        color: colors.surface,
        opacity: 0.95,
        fontSize: 14,
        fontWeight: "600",
        letterSpacing: 0.3
    },
    identityLink: {
        marginTop: 6,
        color: colors.surface,
        opacity: 0.92,
        fontSize: 11,
        fontWeight: "700",
        textDecorationLine: "underline"
    },
    identityInputPill: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        backgroundColor: "rgba(255,255,255,0.95)",
        borderRadius: 14,
        paddingHorizontal: 14,
        paddingVertical: 10,
        marginBottom: 12
    },
    identityInputText: {
        flex: 1,
        fontSize: 16,
        fontWeight: "600",
        color: colors.textPrimary,
        padding: 0
    },
    identityActionRow: {
        flexDirection: "row",
        gap: 8
    },
    identityActionBtn: {
        flex: 1,
        paddingVertical: 12,
        borderRadius: 9999,
        alignItems: "center",
        justifyContent: "center"
    },
    identityActionBtnGhost: {
        backgroundColor: "rgba(255,255,255,0.18)",
        borderWidth: 1.5,
        borderColor: "rgba(255,255,255,0.4)"
    },
    identityActionBtnGhostText: {
        color: colors.surface,
        fontSize: 13,
        fontWeight: "700"
    },
    identityActionBtnSolid: {
        backgroundColor: colors.surface
    },
    identityActionBtnSolidText: {
        color: colors.teal600,
        fontSize: 13,
        fontWeight: "800"
    },
    identityActionBtnDisabled: {
        opacity: 0.6
    },

    // ── ChoiceCard (Yes/No) ────────────────────────────────
    choiceRow: {
        flexDirection: "row",
        gap: 10,
        marginBottom: 4
    },
    choiceCardWrap: {
        flex: 1
    },
    choiceCard: {
        padding: 18,
        borderRadius: 18,
        overflow: "hidden",
        position: "relative",
        minHeight: 132
    },
    choiceCardUnselected: {
        backgroundColor: colors.surface,
        borderWidth: 1.5,
        borderColor: colors.border
    },
    choiceCardYesSelected: {
        shadowColor: colors.teal400,
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.35,
        shadowRadius: 28,
        elevation: 8
    },
    choiceCardNoSelected: {
        backgroundColor: colors.surface,
        borderWidth: 2,
        borderColor: colors.warning500,
        shadowColor: colors.warning500,
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.2,
        shadowRadius: 24,
        elevation: 6
    },
    choiceContent: {
        position: "relative",
        zIndex: 1
    },
    choiceOrbYesSelected: {
        position: "absolute",
        right: -10,
        top: -10,
        width: 70,
        height: 70,
        borderRadius: 999,
        backgroundColor: "rgba(255,255,255,0.15)"
    },
    choiceOrbYesUnselected: {
        position: "absolute",
        right: -10,
        top: -10,
        width: 70,
        height: 70,
        borderRadius: 999,
        backgroundColor: colors.teal50
    },
    choiceOrbNoSelected: {
        position: "absolute",
        right: -10,
        top: -10,
        width: 70,
        height: 70,
        borderRadius: 999,
        backgroundColor: colors.warning50
    },
    choiceOrbNoUnselected: {
        position: "absolute",
        right: -10,
        top: -10,
        width: 70,
        height: 70,
        borderRadius: 999,
        backgroundColor: colors.gray100
    },
    choiceIconYesSelected: {
        width: 36,
        height: 36,
        borderRadius: 12,
        marginBottom: 14,
        backgroundColor: "rgba(255,255,255,0.22)",
        alignItems: "center",
        justifyContent: "center"
    },
    choiceIconYesUnselected: {
        width: 36,
        height: 36,
        borderRadius: 12,
        marginBottom: 14,
        backgroundColor: colors.teal50,
        alignItems: "center",
        justifyContent: "center"
    },
    choiceIconNoSelected: {
        width: 36,
        height: 36,
        borderRadius: 12,
        marginBottom: 14,
        backgroundColor: colors.warning50,
        alignItems: "center",
        justifyContent: "center"
    },
    choiceIconNoUnselected: {
        width: 36,
        height: 36,
        borderRadius: 12,
        marginBottom: 14,
        backgroundColor: colors.gray100,
        alignItems: "center",
        justifyContent: "center"
    },
    choiceTitle: {
        fontSize: 22,
        fontWeight: "800",
        color: colors.textPrimary,
        marginBottom: 2
    },
    choiceTitleSelected: {
        color: colors.surface
    },
    choiceSub: {
        fontSize: 12,
        fontWeight: "600",
        color: colors.textTertiary
    },
    choiceSubYesSelected: {
        color: colors.surface,
        opacity: 0.85
    },
    choiceSubNoSelected: {
        color: colors.warning600
    },

    // ── Absence-reason block ───────────────────────────────
    reasonBlock: {
        marginTop: 16
    },
    reasonLabel: {
        fontSize: 10,
        fontWeight: "700",
        color: colors.textTertiary,
        letterSpacing: 0.8,
        textTransform: "uppercase",
        marginBottom: 8
    },
    reasonLabelRequired: {
        color: colors.error500,
        opacity: 0.9
    },
    reasonChipRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 6,
        marginBottom: 10
    },
    reasonChip: {
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: 11,
        paddingVertical: 5,
        borderRadius: 9999,
        borderWidth: 1.5,
        borderColor: colors.gray200,
        backgroundColor: colors.surface
    },
    reasonChipSelected: {
        borderColor: colors.teal400,
        backgroundColor: colors.teal400
    },
    reasonChipText: {
        fontSize: 12,
        fontWeight: "600",
        color: colors.gray700
    },
    reasonChipTextSelected: {
        color: colors.surface
    },
    reasonChipCheck: {
        fontSize: 12,
        fontWeight: "800",
        color: colors.surface
    },
    reasonNote: {
        borderWidth: 1.5,
        borderStyle: "dashed",
        borderColor: colors.gray300,
        borderRadius: 14,
        padding: 12,
        fontSize: 13,
        fontWeight: "500",
        color: colors.textPrimary,
        minHeight: 70,
        textAlignVertical: "top",
        backgroundColor: colors.surface
    },

    simpleIntro: {
        fontSize: 14,
        fontWeight: "500",
        color: colors.textSecondary,
        lineHeight: 20,
        marginBottom: 16,
        paddingHorizontal: 2
    },

    medCard: {
        backgroundColor: colors.surface,
        borderWidth: 1.5,
        borderColor: colors.border,
        borderRadius: 18,
        padding: 18,
        marginBottom: 14
    },
    medCardComplete: {
        borderColor: colors.teal400
    },
    medCardError: {
        borderColor: colors.error400,
        borderLeftWidth: 3,
        borderLeftColor: colors.error500
    },

    cardHead: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 12,
        gap: 8
    },
    cardCatTitle: {
        fontSize: 11,
        fontWeight: "800",
        color: colors.textPrimary,
        letterSpacing: 0.8,
        textTransform: "uppercase",
        flex: 1
    },

    chipRow: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
    methodRow: { flexDirection: "row", flexWrap: "wrap", gap: 8, marginTop: 8 },
    chip: {
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: 13,
        paddingVertical: 7,
        borderRadius: 9999,
        borderWidth: 1.5,
        borderColor: colors.gray200,
        backgroundColor: colors.surface
    },
    chipDisabled: { opacity: 0.4 },
    chipMethod: { backgroundColor: colors.surface },
    chipText: { fontSize: 13, fontWeight: "600", color: colors.gray700 },
    chipCheck: { fontSize: 13, fontWeight: "700", color: colors.surface },
    chipSelected: {
        backgroundColor: colors.teal400,
        borderColor: colors.teal400
    },
    chipTextSelected: { color: colors.surface },
    chipIcd: {
        backgroundColor: colors.teal50,
        borderColor: colors.teal100
    },
    chipIcdText: { color: colors.teal600 },
    chipIcdSelected: {
        backgroundColor: colors.teal400,
        borderColor: colors.teal400
    },
    chipNeedsSelected: {
        backgroundColor: colors.warning500,
        borderColor: colors.warning500
    },
    chipMethodSelected: {
        backgroundColor: colors.purple,
        borderColor: colors.purple
    },
    chipNaSelected: {
        backgroundColor: colors.gray500,
        borderColor: colors.gray500
    },
    chipViewMore: {
        backgroundColor: colors.surface,
        borderColor: colors.teal400
    },
    chipViewMoreText: { color: colors.teal500, fontWeight: "700" },

    badge: {
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 6,
        flexShrink: 0
    },

    observeBadge: {
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 6,
        backgroundColor: colors.teal50,
        borderWidth: 1,
        borderColor: colors.teal100,
        flexShrink: 0
    },
    observeBadgeText: {
        fontSize: 10,
        fontWeight: "800",
        letterSpacing: 0.6,
        color: colors.teal600
    },
    observeHint: {
        fontSize: 12,
        fontWeight: "500",
        fontStyle: "italic",
        color: colors.teal600,
        marginTop: -2,
        marginBottom: 8
    },
    badgeTeal: { backgroundColor: colors.teal50 },
    badgeAmber: { backgroundColor: colors.warning50 },
    badgeGrey: { backgroundColor: colors.gray100 },
    badgeText: {
        fontSize: 10,
        fontWeight: "700",
        letterSpacing: 0.5,
        textTransform: "uppercase"
    },
    badgeTealText: { color: colors.teal600 },
    badgeAmberText: { color: colors.warning600 },
    badgeGreyText: { color: colors.gray700 },

    fieldLabel: {
        fontSize: 10,
        fontWeight: "700",
        letterSpacing: 0.8,
        textTransform: "uppercase",
        color: colors.textTertiary,
        marginBottom: 6
    },

    manualInput: {
        borderWidth: 1.5,
        borderStyle: "dashed",
        borderColor: colors.gray300,
        borderRadius: 14,
        padding: 12,
        fontSize: 13,
        fontWeight: "500",
        color: colors.textPrimary,
        minHeight: 60,
        textAlignVertical: "top"
    },

    rateRow: {
        paddingVertical: 12,
        borderBottomWidth: 1,
        borderBottomColor: colors.border
    },
    rateRowDirectObs: {
        borderLeftWidth: 3,
        borderLeftColor: colors.teal400,
        paddingLeft: 10,
        marginLeft: -10
    },
    rateRowNameRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 8,
        marginBottom: 8
    },
    rateRowName: {
        flex: 1,
        fontSize: 14,
        fontWeight: "500",
        color: colors.textPrimary,
        lineHeight: 20
    },

    markAllPill: {
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderRadius: 9999,
        borderWidth: 1.5,
        borderColor: colors.teal400
    },
    markAllPillText: { fontSize: 11, fontWeight: "700", color: colors.teal500 },

    sigImage: { width: "100%", height: 100, marginTop: 8 },
    sigPad: {
        marginTop: 8,
        height: 100,
        borderWidth: 1.5,
        borderStyle: "dashed",
        borderColor: colors.gray300,
        borderRadius: 14,
        alignItems: "center",
        justifyContent: "center"
    },
    sigPadText: { fontSize: 13, fontWeight: "600", color: colors.textTertiary },

    // ── StickyBar (container lifted from NarrativeStep) ────
    buttons: {
        flexDirection: "row",
        padding: spacing.base,
        borderTopWidth: 1,
        borderTopColor: colors.border,
        gap: spacing.md,
        backgroundColor: colors.surface
    },
    backButton: {
        flex: 1,
        height: 48,
        borderRadius: radius.sm,
        borderWidth: 1.5,
        borderColor: colors.teal400,
        backgroundColor: colors.surface,
        alignItems: "center",
        justifyContent: "center"
    },
    backButtonText: {
        fontSize: 15,
        fontWeight: "700",
        color: colors.teal500
    },
    nextButton: {
        flex: 2
    },
    nextButtonFull: {
        flex: 1
    },
    nextButtonInner: {
        height: 48,
        borderRadius: radius.sm,
        alignItems: "center",
        justifyContent: "center",
        ...shadows.teal
    },
    nextButtonDisabledInner: {
        backgroundColor: colors.gray200
    },
    nextButtonText: {
        fontSize: 15,
        fontWeight: "700",
        color: colors.surface
    },
    nextButtonTextDisabled: {
        color: colors.textTertiary
    },
    btnDisabled: {
        opacity: 0.5
    },

    snackbar: { backgroundColor: colors.textPrimary }
});
